import React, { useEffect } from "react";
import { motion } from "framer-motion";

const MobileNav = ({ theme, toggleTheme, isOpen, onClose }) => {
  const currentPath = window.location.pathname;

  const links = [
    { name: "Ask / Search", href: "/" },
    { name: "Enterprise AI", href: "/strategy" },
    { name: "Mobile AI", href: "/design" },
    { name: "SMB AI", href: "/technology" },
    { name: "About Us", href: "/about" },
    { name: "Pricing", href: "/pricing" },
    { name: "Contact Us", href: "/contact" }
  ];

  // Focus trap & Escape key listener for mobile drawer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Focus the close button on mount
  useEffect(() => {
    const drawerElement = document.getElementById("mobile-nav-drawer-box");
    if (drawerElement) {
      const closeButton = drawerElement.querySelector("button");
      if (closeButton) {
        const timer = setTimeout(() => closeButton.focus(), 100);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex justify-end"
    >
      {/* Click outside to close */}
      <div className="absolute inset-0" onClick={onClose}></div>

      {/* Drawer panel */}
      <motion.div 
        id="mobile-nav-drawer-box"
        className="relative w-80 max-w-[85vw] h-full bg-white dark:bg-[#202222] border-l border-slate-200 dark:border-slate-800 p-6 flex flex-col justify-between shadow-2xl transition-colors duration-300 focus:outline-none"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
      >
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 pb-4">
            <div className="flex items-center space-x-2">
              <span className="font-bold text-lg bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent logo-font">Jibu Labs</span>
            </div>
            <button 
              onClick={onClose}
              className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              aria-label="Close mobile navigation"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Links */}
          <nav className="flex flex-col space-y-2">
            {links.map((link) => {
              const isActive = currentPath === link.href;
              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={onClose}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                    isActive 
                      ? "bg-forest/10 text-forest dark:text-emerald-400 border border-forest/10" 
                      : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  }`}
                >
                  {link.name}
                </a>
              );
            })}
          </nav>
        </div>

        {/* Footer info & theme toggle inside mobile drawer */}
        <div className="space-y-4 pt-6 border-t border-slate-100 dark:border-slate-800/80">
          <button
            onClick={toggleTheme}
            className="flex items-center justify-between w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-[#191a1a] hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Appearance</span>
            <span className="text-slate-500 text-xs font-semibold text-forest dark:text-emerald-400">
              {theme === "dark" ? "Dark Mode" : "Light Mode"}
            </span>
          </button>
          <div className="text-[10px] text-slate-400 dark:text-slate-500 text-center">
            &copy; {new Date().getFullYear()} Jibu Labs LLC
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MobileNav;